/**
 * OmegaDiscoveryPage — /omega/brain/discovery
 *
 * The Discovery Labs index: a founder-only roll-up of the most recent signed
 * outputs across every discovery lab (`GET /omega/brain/discovery/outputs`).
 * Each row links into that lab's full output stream. White cockpit theme;
 * loading / error / empty states so the page never blanks out.
 */
import { Navigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { apiFetch } from '@/api/http';

interface DiscoveryOutput {
  output_id?: string;
  lab_name: string;
  output_type?: string;
  created_iso: string;
  summary: string;
}

/** The endpoint may return a bare array or `{ outputs: [...] }`; accept both. */
function asOutputs(raw: unknown): DiscoveryOutput[] {
  if (Array.isArray(raw)) return raw as DiscoveryOutput[];
  if (raw && typeof raw === 'object') {
    const v = (raw as Record<string, unknown>)['outputs'];
    if (Array.isArray(v)) return v as DiscoveryOutput[];
  }
  return [];
}

function fmt(iso: string): string {
  try {
    return new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}

export function OmegaDiscoveryPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const outputs = useQuery({
    queryKey: ['discovery', 'outputs'],
    queryFn: () => apiFetch<unknown>('/omega/brain/discovery/outputs'),
  });

  const rows = asOutputs(outputs.data);

  return (
    <div style={{ background: '#f9fafb', minHeight: '100vh', color: '#111827', padding: 24, maxWidth: 900 }}>
      <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>
        <Link to="/omega" style={{ color: '#2563eb' }}>← OMEGA</Link>
      </p>
      <h1 style={{ margin: '0 0 6px', fontSize: 22 }}>Discovery Labs</h1>
      <p style={{ color: '#6b7280', marginBottom: 20 }}>
        Recent signed outputs across every discovery lab.
      </p>

      {outputs.isPending && <p style={{ color: '#6b7280' }}>Loading discovery outputs…</p>}
      {outputs.isError && <p style={{ color: '#dc2626' }}>Discovery outputs unavailable.</p>}
      {!outputs.isPending && !outputs.isError && rows.length === 0 && (
        <p style={{ color: '#6b7280' }}>No discovery lab outputs yet.</p>
      )}

      {rows.length > 0 && (
        <div style={{ display: 'grid', gap: 10 }}>
          {rows.map((o, i) => (
            <Link
              key={o.output_id ?? `${o.lab_name}-${i}`}
              to={`/omega/brain/discovery/${encodeURIComponent(o.lab_name)}/outputs`}
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 4 }}>
                  <span style={{ fontWeight: 600, fontSize: 14 }}>{o.lab_name}</span>
                  <span style={{ fontSize: 12, color: '#6b7280' }}>{fmt(o.created_iso)}</span>
                </div>
                <p style={{ margin: 0, color: '#4b5563', fontSize: 13 }}>
                  {o.summary || o.output_type || 'Discovery output.'}
                </p>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
